import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportGenerator from '../report/form/ReportGenerator';
import StageShell from './common/StageShell';
import { setStage, getProject } from '../../services/sdlcProject.service';
import { getJobStatus } from '../../services/agentcore.service';

/**
 * SDLC – Idea stage.
 *
 * Wraps the existing ReportGenerator so the user can describe an idea and
 * answer the framing questions. When generation completes we persist the
 * resulting report into the SDLC project so downstream stages can consume
 * it without re-fetching from history.
 */
function IdeaStage() {
  const navigate = useNavigate();
  const [project, setProject] = useState(getProject());
  const [pollingJobId, setPollingJobId] = useState(null);
  // 'idle' | 'polling' | 'done' | 'error'
  const [pollingStatus, setPollingStatus] = useState('idle');
  const pollingRef = useRef(null);

  useEffect(() => {
    setProject(getProject());
  }, []);

  // Poll for async job completion after form submits a job
  useEffect(() => {
    if (!pollingJobId) return;
    let alive = true;

    const poll = async () => {
      try {
        const job = await getJobStatus(pollingJobId);
        if (!alive) return;
        if (job.status === 'completed') {
          clearInterval(pollingRef.current);
          setPollingStatus('done');
          const report = job.result?.report || job.result;
          if (report) {
            handleReportGenerated(report);
          }
        } else if (job.status === 'failed') {
          clearInterval(pollingRef.current);
          setPollingStatus('error');
        }
        // still queued/processing — keep polling
      } catch (_) {
        // transient error — keep polling
      }
    };

    // First check after 15 s, then every 10 s
    const timer = setTimeout(() => {
      poll();
      pollingRef.current = setInterval(poll, 10000);
    }, 15000);

    return () => {
      alive = false;
      clearTimeout(timer);
      clearInterval(pollingRef.current);
    };
  }, [pollingJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJobSubmitted = (jobId) => {
    setPollingJobId(jobId);
    setPollingStatus('polling');
  };

  const handleReportGenerated = (data, suggestions = null) => {
    if (suggestions && suggestions.length > 0) {
      // Surface suggestions inline; user can pick one or generate fresh.
      // We don't try to recreate the modal — instead we route to the
      // requirements stage, which knows how to load a report by id.
      const first = suggestions[0];
      const id = first?._id || first?.report_id;
      if (id) {
        setStage('requirements', { reportId: id, snapshot: first });
      }
      navigate('/tools/sdlc/requirements');
      return;
    }
    if (data) {
      const reportId = data?._id || data?.report_id || null;
      setStage('idea', {
        title: data?.title || data?.idea_title || 'Untitled idea',
        summary: data?.summary || data?.idea || '',
      });
      setStage('requirements', { reportId, snapshot: data });
      navigate('/tools/sdlc/requirements');
    }
  };

  return (
    <StageShell
      stageId="idea"
      description="Describe your idea and answer the 8 framing questions. The generated requirements report flows into every downstream stage."
      inputs={[]}
      canContinue={Boolean(project?.idea?.title || project?.requirements?.reportId)}
    >
      {pollingStatus === 'polling' && (
        <div className="stage-empty" style={{ flexDirection: 'column', gap: 12 }}>
          <div className="loading-spinner" style={{ width: 32, height: 32, margin: '0 auto' }} />
          <p style={{ margin: 0 }}>
            AI agents are generating your requirements report. This usually takes 1–3 minutes.
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
            Job ID: <code>{pollingJobId}</code>
          </p>
        </div>
      )}
      {pollingStatus === 'error' && (
        <div className="stage-empty">
          <div className="icon">⚠️</div>
          Report generation failed. Please try again.
        </div>
      )}
      {pollingStatus === 'idle' && (
        <ReportGenerator
          onReportGenerated={handleReportGenerated}
          onJobSubmitted={handleJobSubmitted}
        />
      )}
    </StageShell>
  );
}

export default IdeaStage;
